import { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { InstLayout, Container, Eyebrow, SectionHeading, TrustBar } from "./InstLayout";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

export interface UseCaseTemplateProps {
  seoTitle: string;
  seoDescription: string;
  canonicalUrl: string;
  eyebrow: string;
  h1: ReactNode;
  intro: ReactNode;
  challenge: ReactNode;
  approach: { title: string; description: string }[];
  outcome: ReactNode;
  trustNote: string;
  relatedAudiences: { href: string; label: string }[];
  relatedPlatform: { href: string; label: string };
  relatedUseCases: { href: string; title: string }[];
}

export const UseCaseTemplate = (p: UseCaseTemplateProps) => (
  <InstLayout>
    <SEO title={p.seoTitle} description={p.seoDescription} canonicalUrl={p.canonicalUrl} />
    <Container>
      <header className="max-w-3xl mb-14">
        <Eyebrow>{p.eyebrow}</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6">{p.h1}</h1>
        <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">{p.intro}</p>
      </header>

      <section className="mb-16">
        <SectionHeading eyebrow="The challenge" title="What student success teams are working with today." />
        <div className="text-lg text-foreground leading-relaxed max-w-3xl">{p.challenge}</div>
      </section>

      <section className="mb-16">
        <SectionHeading eyebrow="How CSW supports this" title="What the platform contributes." />
        <ol className="space-y-5">
          {p.approach.map((a, i) => (
            <li key={a.title} className="rounded-2xl border border-border bg-card p-7 flex gap-5">
              <span className="font-display text-2xl font-bold text-primary/70 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3 className="font-display text-lg font-semibold mb-1">{a.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{a.description}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="mb-16">
        <SectionHeading eyebrow="What this looks like in practice" title="The advisor experience." />
        <div className="text-lg text-foreground leading-relaxed max-w-3xl">{p.outcome}</div>
      </section>

      <TrustBar note={p.trustNote} />

      <section className="grid md:grid-cols-3 gap-5 mb-16">
        <div className="rounded-2xl border border-border bg-card p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">Built for</p>
          <ul className="space-y-2">
            {p.relatedAudiences.map((a) => (
              <li key={a.href}>
                <a href={a.href} className="text-foreground hover:text-primary font-medium inline-flex items-center gap-1">
                  {a.label} <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">Powered by</p>
          <a href={p.relatedPlatform.href} className="text-foreground hover:text-primary font-medium inline-flex items-center gap-1">
            {p.relatedPlatform.label} <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">Related use cases</p>
          <ul className="space-y-2">
            {p.relatedUseCases.map((u) => (
              <li key={u.href}>
                <a href={u.href} className="text-foreground hover:text-primary font-medium inline-flex items-center gap-1">
                  {u.title} <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="rounded-3xl border border-border bg-muted/30 p-10 md:p-14 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">See how this works on your data.</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">A 30-minute guided walkthrough with our team.</p>
        <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-base">
          <a href="/demo/">Request a demo</a>
        </Button>
      </section>
    </Container>
  </InstLayout>
);
